import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Workflow as WorkflowIcon, Mail, Clock, Zap } from "lucide-react";
import { API, STAGES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const TRIGGER_TYPES = [
  { id: "stage_change", label: "When stage changes to" },
  { id: "days_since_contact", label: "Days since last contact" },
  { id: "manual", label: "Manual trigger" },
];

export default function Workflows({ user }) {
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    trigger_type: "stage_change",
    trigger_value: "",
    action_type: "send_email",
    template_id: "",
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [workflowsRes, templatesRes] = await Promise.all([
        fetch(`${API}/workflows`, { credentials: "include" }),
        fetch(`${API}/templates`, { credentials: "include" }),
      ]);

      if (workflowsRes.ok) {
        setWorkflows(await workflowsRes.json());
      }
      if (templatesRes.ok) {
        setTemplates(await templatesRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.template_id) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const response = await fetch(`${API}/workflows`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        setWorkflows((prev) => [result, ...prev]);
        setShowModal(false);
        setFormData({
          name: "",
          description: "",
          trigger_type: "stage_change",
          trigger_value: "",
          action_type: "send_email",
          template_id: "",
          is_active: true,
        });
        toast.success("Workflow created");
      } else {
        toast.error("Failed to create workflow");
      }
    } catch (error) {
      toast.error("Failed to create workflow");
    }
  };

  const handleDelete = async (workflowId) => {
    if (!window.confirm("Are you sure you want to delete this workflow?")) return;

    try {
      const response = await fetch(`${API}/workflows/${workflowId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setWorkflows((prev) => prev.filter((w) => w.workflow_id !== workflowId));
        toast.success("Workflow deleted");
      } else {
        toast.error("Failed to delete workflow");
      }
    } catch (error) {
      toast.error("Failed to delete workflow");
    }
  };

  const getTriggerLabel = (type) => {
    return TRIGGER_TYPES.find((t) => t.id === type)?.label || type;
  };

  const getTemplateName = (templateId) => {
    return templates.find((t) => t.template_id === templateId)?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflows-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            Email Workflows
          </h1>
          <p className="text-slate-500 mt-1">
            Automate your email outreach based on triggers
          </p>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => setShowModal(true)}
          data-testid="create-workflow-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Workflow
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">How Workflows Work</h3>
              <p className="text-sm text-slate-600 mt-1">
                Workflows automatically send emails when specific triggers occur. For example:
              </p>
              <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc list-inside">
                <li>Send a follow-up email when a client moves to "First Contact" stage</li>
                <li>Send a re-engagement email if no contact in 30 days</li>
                <li>Trigger welcome emails for new leads</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Workflows List */}
      <div className="space-y-4">
        {workflows.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <WorkflowIcon className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                No workflows yet. Create your first automation to save time on outreach.
              </p>
              <Button
                className="mt-4 bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowModal(true)}
              >
                Create Workflow
              </Button>
            </CardContent>
          </Card>
        ) : (
          workflows.map((workflow) => (
            <Card
              key={workflow.workflow_id}
              className="hover:shadow-md transition-shadow"
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        workflow.is_active
                          ? "bg-emerald-100"
                          : "bg-slate-100"
                      }`}
                    >
                      <WorkflowIcon
                        className={`w-5 h-5 ${
                          workflow.is_active
                            ? "text-emerald-600"
                            : "text-slate-400"
                        }`}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">
                          {workflow.name}
                        </h3>
                        <Badge
                          variant={workflow.is_active ? "default" : "secondary"}
                          className={
                            workflow.is_active
                              ? "bg-emerald-100 text-emerald-700"
                              : ""
                          }
                        >
                          {workflow.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      {workflow.description && (
                        <p className="text-sm text-slate-500 mt-1">
                          {workflow.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Clock className="w-4 h-4" />
                          <span>
                            {getTriggerLabel(workflow.trigger_type)}
                            {workflow.trigger_value && `: ${workflow.trigger_value}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Mail className="w-4 h-4" />
                          <span>Template: {getTemplateName(workflow.template_id)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(workflow.workflow_id)}
                    data-testid={`delete-workflow-${workflow.workflow_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Workflow Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Workflow</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Workflow Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., First Contact Follow-up"
                data-testid="workflow-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="What does this workflow do?"
                rows={2}
              />
            </div>
            <div>
              <Label>Trigger Type</Label>
              <Select
                value={formData.trigger_type}
                onValueChange={(v) =>
                  setFormData({ ...formData, trigger_type: v, trigger_value: "" })
                }
              >
                <SelectTrigger data-testid="workflow-trigger-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.trigger_type === "stage_change" && (
              <div>
                <Label>Target Stage</Label>
                <Select
                  value={formData.trigger_value}
                  onValueChange={(v) =>
                    setFormData({ ...formData, trigger_value: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {formData.trigger_type === "days_since_contact" && (
              <div>
                <Label>Days</Label>
                <Input
                  type="number"
                  value={formData.trigger_value}
                  onChange={(e) =>
                    setFormData({ ...formData, trigger_value: e.target.value })
                  }
                  placeholder="e.g., 7"
                />
              </div>
            )}
            <div>
              <Label>Email Template *</Label>
              <Select
                value={formData.template_id}
                onValueChange={(v) =>
                  setFormData({ ...formData, template_id: v })
                }
              >
                <SelectTrigger data-testid="workflow-template-select">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) =>
                  setFormData({ ...formData, is_active: v })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleCreate}
              data-testid="save-workflow-btn"
            >
              Create Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
